import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiShield, FiCheck, FiEye, FiEyeOff, FiArrowLeft, FiArrowRight } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { loginAdmin, resendVerification, checkAdminAvailability } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { StateCitySelect } from '../../components/common';
import styles from './AdminAuth.module.css';

const FEATURES = [
  'Full control over your Derasar',
  'Manage members & approvals',
  'Donation categories & ledger',
  'Expense tracking & reports',
  'Financial year management',
  'Role-based access control',
];

const AdminAuth = () => {
  const { loginUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [tab, setTab]                   = useState(() => searchParams.get('tab') === 'register' ? 'register' : 'login');
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState('');
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [resendLoading, setResendLoading]   = useState(false);
  const [showPassword, setShowPassword]     = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [form, setForm] = useState({
    name: '', identifier: '', phone: '', password: '', confirmPassword: '',
    derasarName: '', derasarCity: '', derasarDescription: '',
  });
  const [derasarState, setDerasarState] = useState('');

  const handleChange = (e) => {
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (tab === 'login') {
        const res = await loginAdmin({ identifier: form.identifier, password: form.password });
        if (!res.data.isEmailVerified) {
          loginUser(res.data);
          setUnverifiedUser(res.data);
          return;
        }
        loginUser(res.data);
        navigate('/dashboard');
      } else {
        if (form.password !== form.confirmPassword) {
          setError('Passwords do not match.');
          setLoading(false);
          return;
        }
        if (!derasarState.trim()) {
          setError('Please select or enter a state for your Derasar.');
          setLoading(false);
          return;
        }
        if (!form.derasarCity.trim()) {
          setError('Please select or enter a city for your Derasar.');
          setLoading(false);
          return;
        }
        await checkAdminAvailability({ email: form.identifier, phone: form.phone });
        navigate('/payment', {
          state: {
            formData: {
              name: form.name,
              email: form.identifier,
              phone: form.phone,
              password: form.password,
              derasarName: form.derasarName,
              derasarCity: form.derasarCity,
              derasarState: derasarState,
              derasarDescription: form.derasarDescription,
            },
          },
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.msg || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendLoading(true);
    try {
      await resendVerification();
      toast.success('Verification email sent! Please check your inbox.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to resend. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className={styles.page}>

      {/* ── Left panel — indigo ── */}
      <div className={styles.leftPanel}>
        <div className={styles.brandBadge}>
          <FiShield size={11} /> Admin Portal
        </div>
        <div className={styles.brandName}>Derasar<br />Admin Portal</div>
        <p className={styles.brandTagline}>
          Register and manage your Jain Derasar — memberships, donations, accounts, and reports in one place.
        </p>
        <div className={styles.features}>
          {FEATURES.map(f => (
            <div key={f} className={styles.featureItem}>
              <div className={styles.featureIcon}><FiCheck size={13} /></div>
              {f}
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel — form ── */}
      <div className={styles.rightPanel}>
        <div className={styles.formContainer}>

          <Link to="/" className={styles.backLink}>
            <FiArrowLeft size={13} /> Back to home
          </Link>

          <h2 className={styles.formTitle}>
            {tab === 'login' ? 'Admin Sign In' : 'Register Your Derasar'}
          </h2>
          <p className={styles.formSubtitle}>
            {tab === 'login'
              ? 'Sign in to manage your Derasar'
              : 'Create your admin account and set up your Derasar'}
          </p>

          <div className={styles.authTabs}>
            {[{ id: 'login', label: 'Sign In' }, { id: 'register', label: 'Register Derasar' }].map(t => (
              <button
                key={t.id}
                type="button"
                className={`${styles.authTab} ${tab === t.id ? styles.active : ''}`}
                onClick={() => { setTab(t.id); setError(''); }}
              >
                {t.label}
              </button>
            ))}
          </div>

          {unverifiedUser && (
            <div className={styles.verifyBanner}>
              <strong>Please verify your email</strong>
              <p>We sent a verification link to <strong>{unverifiedUser.email}</strong>. You must verify before accessing all features.</p>
              <div className={styles.verifyBannerActions}>
                <button type="button" className="btn btn-primary btn-sm" onClick={handleResendVerification} disabled={resendLoading}>
                  {resendLoading ? 'Sending…' : 'Resend Email'}
                </button>
              </div>
            </div>
          )}

          {!unverifiedUser && error && <div className={styles.errorMsg}>{error}</div>}

          {!unverifiedUser && (
            <form onSubmit={handleSubmit}>
              {tab === 'register' && (
                <div className="form-group">
                  <label>Your Full Name *</label>
                  <input name="name" value={form.name} onChange={handleChange} placeholder="e.g. Mahesh Jain" required />
                </div>
              )}

              <div className="form-group">
                <label>{tab === 'login' ? 'Email or Phone Number *' : 'Email Address *'}</label>
                <input
                  name="identifier"
                  type={tab === 'register' ? 'email' : 'text'}
                  value={form.identifier}
                  onChange={handleChange}
                  placeholder={tab === 'login' ? 'Enter email or phone number' : 'Enter email address'}
                  required
                />
                {tab === 'login' && (
                  <small style={{ color: 'var(--color-text-muted)', fontSize: '0.75rem' }}>
                    You can sign in with either your email or mobile number
                  </small>
                )}
              </div>

              {tab === 'register' && (
                <div className="form-group">
                  <label>Mobile Number *</label>
                  <input name="phone" type="tel" value={form.phone} onChange={handleChange} placeholder="Enter 10-digit mobile number" required />
                </div>
              )}

              <div className="form-group" style={{ position: 'relative' }}>
                <label>Password *</label>
                <input
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={handleChange}
                  required
                  minLength={6}
                  style={{ paddingRight: '40px' }}
                />
                <span
                  onClick={() => setShowPassword(s => !s)}
                  style={{ position: 'absolute', right: 12, top: 36, cursor: 'pointer', color: 'var(--color-text-muted)' }}
                >
                  {showPassword ? <FiEye /> : <FiEyeOff />}
                </span>
                {tab === 'register' && form.password.length > 0 && form.password.length < 6 && (
                  <small style={{ color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: 4 }}>
                    Password must be at least 6 characters
                  </small>
                )}
              </div>

              {tab === 'register' && (
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>Confirm Password *</label>
                  <input
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={form.confirmPassword}
                    onChange={handleChange}
                    required
                    minLength={6}
                    style={{ paddingRight: '40px' }}
                  />
                  <span
                    onClick={() => setShowConfirmPassword(s => !s)}
                    style={{ position: 'absolute', right: 12, top: 36, cursor: 'pointer', color: 'var(--color-text-muted)' }}
                  >
                    {showConfirmPassword ? <FiEye /> : <FiEyeOff />}
                  </span>
                  {form.confirmPassword.length > 0 && form.confirmPassword !== form.password && (
                    <small style={{ color: '#dc2626', fontSize: '0.75rem', display: 'block', marginTop: 4 }}>
                      Passwords do not match
                    </small>
                  )}
                </div>
              )}

              {tab === 'login' && (
                <div style={{ textAlign: 'right' }}>
                  <Link to="/forgot-password" className={styles.forgotLink}>Forgot password?</Link>
                </div>
              )}

              {tab === 'register' && (
                <>
                  <div className={styles.sectionLabel}>Derasar Information</div>
                  <div className="form-group">
                    <label>Derasar Name *</label>
                    <input name="derasarName" value={form.derasarName} onChange={handleChange} placeholder="e.g. Shri Mahavir Derasar" required />
                  </div>
                  <StateCitySelect
                    state={derasarState}
                    city={form.derasarCity}
                    onStateChange={setDerasarState}
                    onCityChange={(val) => setForm((f) => ({ ...f, derasarCity: val }))}
                    required
                  />
                  <div className="form-group">
                    <label>Description</label>
                    <textarea name="derasarDescription" value={form.derasarDescription} onChange={handleChange} rows={2} placeholder="Brief description of your Derasar" />
                  </div>
                  <div className={styles.pendingBanner}>
                    Next step: Select a subscription plan and complete payment. Your Derasar will then be reviewed by the super admin.
                  </div>
                </>
              )}

              <button type="submit" className={styles.submitBtn} disabled={loading}>
                {loading ? 'Please wait…' : tab === 'login' ? 'Sign In' : <>Continue to Payment <FiArrowRight size={14} /></>}
              </button>
            </form>
          )}

          <div className={styles.switchLink}>
            <Link to="/login">← Switch to Member Login</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAuth;
